import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  FileText, Receipt, Landmark, Wallet, Layers, FileDown, ChevronDown,
  CheckCircle2, Circle, Info, Mail, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { invoicesApi, documentsApi } from "../../lib/api";

// ---------- helpers ----------
function money2(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}
function dayKey(iso) {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(filename, headers, rows) {
  const body = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
}

const isOutstanding = (inv) => inv.status !== "Paid" && inv.status !== "Voided" && (Number(inv.balanceDue) || 0) > 0.005;
const isLive = (inv) => inv.status !== "Voided";

// Aging buckets (user choice): Current · 1-30 · 31-60 (and 60+ overflow shown in table).
function agingBucket(inv) {
  const basis = inv.dueAt || inv.issuedAt;
  if (!basis) return "current";
  const days = Math.floor((Date.now() - new Date(basis.length === 10 ? `${basis}T00:00:00` : basis).getTime()) / 86400000);
  if (days <= 0) return "current";
  if (days <= 30) return "1-30";
  if (days <= 60) return "31-60";
  return "60+";
}
const BUCKETS = [
  { id: "current", label: "Current", tone: "slate" },
  { id: "1-30", label: "1-30 Days", tone: "amber" },
  { id: "31-60", label: "31-60 Days", tone: "rose" },
];

const NAV = [
  { id: "batch", label: "Batch/Export Transactions", icon: Layers },
  { id: "ar-management", label: "AR Management", icon: Landmark },
  { id: "invoices", label: "Invoices", icon: FileText },
  { id: "payments", label: "Customer Payments", icon: Wallet },
  { id: "deposits", label: "Bank Deposits", icon: Receipt },
];

export default function AccountingSection({ invoices = [], customers = [], reloadInvoices, onOpenInvoice, fromDate, toDate }) {
  const [section, setSection] = useState("invoices");
  const emailById = useMemo(() => {
    const m = {};
    customers.forEach((c) => { m[c.id] = (c.email || "").trim(); });
    return m;
  }, [customers]);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex min-h-[560px]" data-testid="accounting-section">
      {/* Left sub-nav */}
      <aside className="w-56 shrink-0 border-r border-slate-100 bg-slate-50/40 py-4">
        <div className="px-4 text-[16px] font-bold text-slate-900 mb-3">Accounting</div>
        <div className="px-4 text-[10.5px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Accounts Receivable</div>
        <nav className="mb-2">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setSection(n.id)}
              data-testid={`accounting-nav-${n.id}`}
              className={`w-full flex items-center gap-2 px-4 py-2 text-left text-[13px] transition-colors ${
                section === n.id ? "text-blue-700 font-semibold bg-blue-50/70 border-r-2 border-blue-600" : "text-slate-600 hover:bg-slate-100/70"
              }`}
            >
              <n.icon className="h-3.5 w-3.5 shrink-0" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="px-4 text-[10.5px] uppercase tracking-wider font-bold text-slate-300 mt-4 mb-1.5">Accounts Payable</div>
        <div className="px-4 text-[11.5px] text-slate-300 italic">Coming soon</div>
      </aside>

      {/* Main panel */}
      <div className="flex-1 min-w-0 p-5">
        {section === "invoices" && (
          <InvoicesPanel invoices={invoices} reloadInvoices={reloadInvoices} onOpenInvoice={onOpenInvoice} emailById={emailById} />
        )}
        {section === "ar-management" && <ARManagementPanel invoices={invoices} />}
        {section === "payments" && <CustomerPaymentsPanel invoices={invoices} onOpenInvoice={onOpenInvoice} />}
        {section === "deposits" && <BankDepositsPanel invoices={invoices} />}
        {section === "batch" && <BatchExportPanel invoices={invoices} fromDate={fromDate} toDate={toDate} />}
      </div>
    </div>
  );
}

// ============================ INVOICES ============================
function InvoicesPanel({ invoices, reloadInvoices, onOpenInvoice, emailById = {} }) {
  const [tab, setTab] = useState("aging"); // review | aging | paid
  const [filter, setFilter] = useState(null); // bucket id OR review/paid filter
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [reminding, setReminding] = useState(() => new Set()); // ids currently sending
  const [reminded, setReminded] = useState(() => new Set()); // ids reminded this session

  const live = useMemo(() => invoices.filter(isLive), [invoices]);
  const outstanding = useMemo(() => invoices.filter(isOutstanding), [invoices]);

  // reset filter+selection when switching tabs
  useEffect(() => { setFilter(null); setSelected(new Set()); }, [tab]);

  // ---- summary per tab ----
  const agingSummary = useMemo(() => {
    const m = { current: { total: 0, sent: 0, notSent: 0 }, "1-30": { total: 0, sent: 0, notSent: 0 }, "31-60": { total: 0, sent: 0, notSent: 0 } };
    outstanding.forEach((inv) => {
      const b = agingBucket(inv);
      if (!m[b]) return; // 60+ not carded
      m[b].total += Number(inv.balanceDue) || 0;
      if (inv.lastSentAt) m[b].sent += 1; else m[b].notSent += 1;
    });
    return m;
  }, [outstanding]);

  const reviewSummary = useMemo(() => {
    let reviewed = { total: 0, count: 0 }, pending = { total: 0, count: 0 };
    live.forEach((inv) => {
      const t = Number(inv.total) || 0;
      if (inv.reviewed) { reviewed.total += t; reviewed.count += 1; }
      else { pending.total += t; pending.count += 1; }
    });
    return { reviewed, pending };
  }, [live]);

  const paidSummary = useMemo(() => {
    const m = { Paid: { total: 0, count: 0 }, "Partially Paid": { total: 0, count: 0 }, Unpaid: { total: 0, count: 0 } };
    live.forEach((inv) => {
      const k = m[inv.status] ? inv.status : "Unpaid";
      m[k].total += Number(inv.total) || 0;
      m[k].count += 1;
    });
    return m;
  }, [live]);

  // ---- visible rows ----
  const rows = useMemo(() => {
    let list;
    if (tab === "aging") {
      list = outstanding.filter((inv) => !filter || agingBucket(inv) === filter);
    } else if (tab === "review") {
      list = live.filter((inv) => !filter || (filter === "reviewed" ? inv.reviewed : !inv.reviewed));
    } else {
      list = live.filter((inv) => !filter || (inv.status || "Unpaid") === filter);
    }
    return [...list].sort((a, b) => String(b.issuedAt).localeCompare(String(a.issuedAt)));
  }, [tab, filter, outstanding, live]);

  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectedRows = rows.filter((r) => selected.has(r.id));

  const exportCsv = () => {
    const src = selectedRows.length ? selectedRows : rows;
    const headers = ["Invoice Date", "Invoice #", "Customer Name", "Customer #", "Total", "Balance", "Status", "Reviewed", "Sent"];
    const data = src.map((r) => [
      fmtDate(r.issuedAt), r.number || r.id, r.customer, r.customerId,
      (Number(r.total) || 0).toFixed(2), (Number(r.balanceDue) || 0).toFixed(2),
      r.status, r.reviewed ? "Yes" : "No", r.lastSentAt ? "Yes" : "No",
    ]);
    downloadCsv(`invoices-${tab}.csv`, headers, data);
    toast.success(`Exported ${data.length} invoice${data.length === 1 ? "" : "s"}`);
  };

  const markReviewed = async (reviewed) => {
    if (!selectedRows.length) { toast.error("Select at least one invoice"); return; }
    setBusy(true);
    try {
      const res = await invoicesApi.markReviewed(selectedRows.map((r) => r.id), reviewed);
      toast.success(`${res.updated} invoice${res.updated === 1 ? "" : "s"} marked ${reviewed ? "reviewed" : "not reviewed"}`);
      setSelected(new Set());
      reloadInvoices?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  // ---- one-click email reminder (reuses the branded invoice email w/ PDF + pay link) ----
  const buildReminder = (inv) => {
    const to = emailById[inv.customerId] || "";
    const payLink = `${window.location.origin}/pay/${inv.id}`;
    const subject = `Payment reminder: Invoice ${inv.number || inv.id} — ${money2(inv.balanceDue)} due`;
    const body =
      `Hi ${inv.customer || "there"},\n\n` +
      `This is a friendly reminder that invoice ${inv.number || inv.id} has an outstanding balance of ${money2(inv.balanceDue)}.\n` +
      `You can view the attached invoice and pay securely online using the button below. ` +
      `If you've already sent payment, please disregard this message.\n\n` +
      `Thank you!`;
    return { to, payLink, subject, body };
  };

  const sendReminder = async (inv, { silent = false } = {}) => {
    const { to, payLink, subject, body } = buildReminder(inv);
    if (!to) { if (!silent) toast.error(`No email on file for ${inv.customer}`); return false; }
    setReminding((s) => new Set(s).add(inv.id));
    try {
      const res = await documentsApi.emailInvoice(inv.id, { to, subject, body, pay_link: payLink });
      if (res.sent) {
        if (!silent) toast.success(`Reminder sent to ${to}`);
        setReminded((s) => new Set(s).add(inv.id));
        return true;
      }
      if (res.reason === "smtp_not_configured") {
        if (!silent) toast.error("Email isn't set up. Go to Company Settings → Email sending (SMTP) to connect your server.");
        return false;
      }
      if (!silent) toast.error(res.reason || "Reminder failed to send");
      return false;
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "Reminder failed to send");
      return false;
    } finally {
      setReminding((s) => { const n = new Set(s); n.delete(inv.id); return n; });
    }
  };

  const sendRemindersBulk = async () => {
    const targets = selectedRows.filter(isOutstanding);
    const withEmail = targets.filter((inv) => emailById[inv.customerId]);
    if (targets.length === 0) { toast.error("Select unpaid invoices to remind"); return; }
    if (withEmail.length === 0) { toast.error("None of the selected invoices have a customer email on file"); return; }
    if (!window.confirm(`Send a payment reminder email to ${withEmail.length} customer${withEmail.length === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    let ok = 0;
    for (const inv of withEmail) {
      // eslint-disable-next-line no-await-in-loop
      if (await sendReminder(inv, { silent: true })) ok += 1;
    }
    const skipped = targets.length - withEmail.length;
    toast.success(`Sent ${ok} reminder${ok === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped (no email)` : ""}`);
    setBusy(false);
    reloadInvoices?.();
  };

  const TABS = [
    { id: "review", label: "Review Status" },
    { id: "aging", label: "Invoice Aging" },
    { id: "paid", label: "Paid Status" },
  ];

  return (
    <div data-testid="accounting-invoices-panel">
      <div className="text-[22px] font-bold text-slate-900 mb-3">Invoices</div>

      {/* sub-tabs */}
      <div className="flex items-center gap-6 border-b border-slate-200 mb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`accounting-invoices-tab-${t.id}`}
            className={`relative -mb-px pb-2 text-[14px] transition-colors ${
              tab === t.id ? "text-slate-900 font-semibold border-b-2 border-blue-600" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* summary cards */}
      {tab === "aging" && (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 mb-4">
          <div className="grid grid-cols-3 gap-6">
            {BUCKETS.map((b) => {
              const s = agingSummary[b.id];
              const totalCount = s.sent + s.notSent;
              const sentPct = totalCount ? (s.sent / totalCount) * 100 : 0;
              const active = filter === b.id;
              const fill = b.tone === "slate" ? "#475569" : b.tone === "amber" ? "#FB923C" : "#EF4444";
              const track = b.tone === "slate" ? "#CBD5E1" : b.tone === "amber" ? "#FED7AA" : "#FECACA";
              return (
                <button
                  key={b.id}
                  onClick={() => setFilter(active ? null : b.id)}
                  data-testid={`accounting-aging-card-${b.id}`}
                  className={`text-left rounded-lg px-3 py-2 transition-colors ${active ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-slate-50"}`}
                >
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{b.label}</div>
                  <div className="text-[24px] font-bold text-slate-900 leading-tight">{money2(s.total)}</div>
                  <div className="mt-2 h-2.5 rounded-full overflow-hidden flex" style={{ background: track }}>
                    <div style={{ width: `${sentPct}%`, background: fill }} />
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[11.5px]">
                    <span><strong className="text-slate-900">{s.notSent}</strong> <span className="text-slate-400">Not Sent</span></span>
                    <span><strong className="text-slate-900">{s.sent}</strong> <span className="text-slate-400">Sent</span></span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === "review" && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <FilterCard testid="accounting-review-card-pending" active={filter === "pending"} onClick={() => setFilter(filter === "pending" ? null : "pending")}
            label="Needs Review" value={money2(reviewSummary.pending.total)} sub={`${reviewSummary.pending.count} invoices`} tone="amber" />
          <FilterCard testid="accounting-review-card-reviewed" active={filter === "reviewed"} onClick={() => setFilter(filter === "reviewed" ? null : "reviewed")}
            label="Reviewed" value={money2(reviewSummary.reviewed.total)} sub={`${reviewSummary.reviewed.count} invoices`} tone="emerald" />
        </div>
      )}

      {tab === "paid" && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <FilterCard testid="accounting-paid-card-unpaid" active={filter === "Unpaid"} onClick={() => setFilter(filter === "Unpaid" ? null : "Unpaid")}
            label="Unpaid" value={money2(paidSummary.Unpaid.total)} sub={`${paidSummary.Unpaid.count} invoices`} tone="rose" />
          <FilterCard testid="accounting-paid-card-partial" active={filter === "Partially Paid"} onClick={() => setFilter(filter === "Partially Paid" ? null : "Partially Paid")}
            label="Partially Paid" value={money2(paidSummary["Partially Paid"].total)} sub={`${paidSummary["Partially Paid"].count} invoices`} tone="amber" />
          <FilterCard testid="accounting-paid-card-paid" active={filter === "Paid"} onClick={() => setFilter(filter === "Paid" ? null : "Paid")}
            label="Paid" value={money2(paidSummary.Paid.total)} sub={`${paidSummary.Paid.count} invoices`} tone="emerald" />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mb-2">
        <ActionsMenu
          selectedCount={selectedRows.length}
          reminderCount={selectedRows.filter(isOutstanding).length}
          onExport={exportCsv}
          onMarkReviewed={() => markReviewed(true)}
          onMarkNotReviewed={() => markReviewed(false)}
          onSendReminders={sendRemindersBulk}
          busy={busy}
        />
        {selectedRows.length > 0 && (
          <span className="text-[12px] text-slate-500" data-testid="accounting-selected-count">{selectedRows.length} selected</span>
        )}
        {filter && (
          <button onClick={() => setFilter(null)} data-testid="accounting-clear-filter" className="text-[12px] text-blue-600 hover:underline ml-auto">Clear filter</button>
        )}
      </div>

      {/* Table */}
      <InvoiceTable
        rows={rows}
        tab={tab}
        selected={selected}
        allVisibleSelected={allVisibleSelected}
        toggleAll={toggleAll}
        toggleOne={toggleOne}
        onOpenInvoice={onOpenInvoice}
        onRemind={sendReminder}
        reminding={reminding}
        reminded={reminded}
        emailById={emailById}
      />
    </div>
  );
}

function FilterCard({ label, value, sub, tone, active, onClick, testid }) {
  const tones = {
    amber: "text-amber-700", emerald: "text-emerald-700", rose: "text-rose-700", slate: "text-slate-700",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left rounded-xl border px-4 py-3 transition-colors ${active ? "border-blue-300 bg-blue-50/60 ring-1 ring-blue-200" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`text-[20px] font-bold ${tones[tone] || "text-slate-900"}`}>{value}</div>
      <div className="text-[11.5px] text-slate-400">{sub}</div>
    </button>
  );
}

function InvoiceTable({ rows, tab, selected, allVisibleSelected, toggleAll, toggleOne, onOpenInvoice, onRemind, reminding, reminded, emailById = {} }) {
  const statusPill = (inv) => {
    if (tab === "aging") {
      const b = agingBucket(inv);
      const label = b === "current" ? "Current" : b === "60+" ? "60+ Days" : `${b} Days`;
      const cls = b === "current" ? "bg-slate-100 text-slate-600" : b === "1-30" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700";
      return <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
    }
    if (tab === "review") {
      return inv.reviewed
        ? <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Reviewed</span>
        : <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700"><Circle className="h-3 w-3" /> Needs review</span>;
    }
    const cls = inv.status === "Paid" ? "bg-emerald-50 text-emerald-700" : inv.status === "Partially Paid" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700";
    return <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{inv.status}</span>;
  };

  if (rows.length === 0) {
    return <div className="border border-slate-200 rounded-lg py-12 text-center text-[13px] text-slate-400 italic">No invoices to show.</div>;
  }
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-[13px]">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            <th className="px-3 py-2.5 w-10">
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="accounting-select-all" className="h-3.5 w-3.5 accent-blue-600" />
            </th>
            <th className="px-3 py-2.5">Invoice Date</th>
            <th className="px-3 py-2.5">Invoice #</th>
            <th className="px-3 py-2.5">Customer Name</th>
            <th className="px-3 py-2.5">Customer #</th>
            <th className="px-3 py-2.5 text-right">Balance</th>
            <th className="px-3 py-2.5">Status</th>
            <th className="px-3 py-2.5 text-right w-24">Reminder</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((inv) => (
            <tr key={inv.id} data-testid={`accounting-invoice-row-${inv.id}`} className="hover:bg-blue-50/30">
              <td className="px-3 py-2.5">
                <input type="checkbox" checked={selected.has(inv.id)} onChange={() => toggleOne(inv.id)} data-testid={`accounting-row-check-${inv.id}`} className="h-3.5 w-3.5 accent-blue-600" />
              </td>
              <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{fmtDate(inv.issuedAt)}</td>
              <td className="px-3 py-2.5">
                <button onClick={() => onOpenInvoice?.(inv.id)} data-testid={`accounting-open-invoice-${inv.id}`} className="text-blue-600 font-semibold hover:underline">{inv.number || inv.id}</button>
              </td>
              <td className="px-3 py-2.5">
                <button onClick={() => onOpenInvoice?.(inv.id)} className="text-blue-600 hover:underline truncate max-w-[200px] text-left">{inv.customer}</button>
              </td>
              <td className="px-3 py-2.5 text-slate-500 font-mono text-[12px]">{inv.customerId}</td>
              <td className="px-3 py-2.5 text-right font-semibold text-slate-900 whitespace-nowrap">{money2(inv.balanceDue)}</td>
              <td className="px-3 py-2.5">{statusPill(inv)}</td>
              <td className="px-3 py-2.5 text-right">
                {isOutstanding(inv) ? <ReminderButton inv={inv} onRemind={onRemind} busy={reminding?.has(inv.id)} done={reminded?.has(inv.id)} hasEmail={!!emailById[inv.customerId]} /> : <span className="text-slate-300">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReminderButton({ inv, onRemind, busy, done, hasEmail }) {
  return (
    <button
      onClick={() => onRemind?.(inv)}
      disabled={busy || !hasEmail}
      data-testid={`accounting-remind-${inv.id}`}
      title={!hasEmail ? "No email on file for this customer" : done ? "Reminder sent — click to send again" : "Email payment reminder"}
      className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[11.5px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        done ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-slate-200 text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
      }`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
      {busy ? "Sending" : done ? "Sent" : "Remind"}
    </button>
  );
}

function ActionsMenu({ selectedCount, reminderCount = 0, onExport, onMarkReviewed, onMarkNotReviewed, onSendReminders, busy }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const run = (fn) => { setOpen(false); fn(); };
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="accounting-actions-btn"
        disabled={busy}
        className="h-8 px-3 rounded-md border border-slate-300 bg-slate-50 hover:bg-slate-100 text-[13px] font-semibold text-slate-700 flex items-center gap-1.5 disabled:opacity-50"
      >
        Actions <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-9 z-20 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-1" data-testid="accounting-actions-menu">
          <button onClick={() => run(onExport)} data-testid="accounting-action-export" className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50">
            <FileDown className="h-3.5 w-3.5" /> Export to CSV{selectedCount ? ` (${selectedCount})` : ""}
          </button>
          <button onClick={() => run(onSendReminders)} disabled={!reminderCount} data-testid="accounting-action-send-reminders" className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <Mail className="h-3.5 w-3.5" /> Send payment reminders{reminderCount ? ` (${reminderCount})` : ""}
          </button>
          <button onClick={() => run(onMarkReviewed)} disabled={!selectedCount} data-testid="accounting-action-mark-reviewed" className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark reviewed
          </button>
          <button onClick={() => run(onMarkNotReviewed)} disabled={!selectedCount} data-testid="accounting-action-mark-not-reviewed" className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <Circle className="h-3.5 w-3.5" /> Mark not reviewed
          </button>
        </div>
      )}
    </div>
  );
}

// ============================ AR MANAGEMENT ============================
function ARManagementPanel({ invoices }) {
  const data = useMemo(() => {
    const outstanding = invoices.filter(isOutstanding);
    const buckets = { current: 0, "1-30": 0, "31-60": 0, "60+": 0 };
    const byCustomer = new Map();
    let totalReceivable = 0;
    outstanding.forEach((inv) => {
      const bal = Number(inv.balanceDue) || 0;
      totalReceivable += bal;
      buckets[agingBucket(inv)] += bal;
      const c = byCustomer.get(inv.customerId) || { customerId: inv.customerId, name: inv.customer, balance: 0, count: 0 };
      c.balance += bal; c.count += 1;
      byCustomer.set(inv.customerId, c);
    });
    const pastDue = buckets["1-30"] + buckets["31-60"] + buckets["60+"];
    const topCustomers = Array.from(byCustomer.values()).sort((a, b) => b.balance - a.balance).slice(0, 8);
    return { totalReceivable, current: buckets.current, pastDue, buckets, openCount: outstanding.length, topCustomers };
  }, [invoices]);

  const exportCsv = () => {
    downloadCsv("ar-aging-summary.csv",
      ["Bucket", "Balance"],
      [["Current", data.current.toFixed(2)], ["1-30 Days", data.buckets["1-30"].toFixed(2)], ["31-60 Days", data.buckets["31-60"].toFixed(2)], ["60+ Days", data.buckets["60+"].toFixed(2)], ["Total Receivable", data.totalReceivable.toFixed(2)]]);
    toast.success("Exported AR summary");
  };

  const maxBucket = Math.max(1, data.current, data.buckets["1-30"], data.buckets["31-60"], data.buckets["60+"]);
  const bars = [
    { label: "Current", val: data.current, color: "#475569" },
    { label: "1-30 Days", val: data.buckets["1-30"], color: "#FB923C" },
    { label: "31-60 Days", val: data.buckets["31-60"], color: "#F87171" },
    { label: "60+ Days", val: data.buckets["60+"], color: "#DC2626" },
  ];
  return (
    <div data-testid="accounting-ar-management-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[22px] font-bold text-slate-900">AR Management</div>
        <ExportBtn onClick={exportCsv} />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        <BigStat label="Total Receivable" value={money2(data.totalReceivable)} accent="text-slate-900" />
        <BigStat label="Current" value={money2(data.current)} accent="text-emerald-700" />
        <BigStat label="Past Due" value={money2(data.pastDue)} accent="text-rose-700" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="text-[13px] font-semibold text-slate-900 mb-3">Aging breakdown</div>
          <div className="space-y-2.5">
            {bars.map((b) => (
              <div key={b.label}>
                <div className="flex items-center justify-between text-[12px] mb-0.5">
                  <span className="text-slate-600">{b.label}</span>
                  <span className="font-semibold text-slate-900">{money2(b.val)}</span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(b.val / maxBucket) * 100}%`, background: b.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 text-[13px] font-semibold text-slate-900">Top customers by open balance</div>
          {data.topCustomers.length === 0 ? (
            <div className="py-8 text-center text-[12.5px] text-slate-400 italic">No open balances. 🎉</div>
          ) : (
            <table className="w-full text-[13px]">
              <tbody className="divide-y divide-slate-100">
                {data.topCustomers.map((c) => (
                  <tr key={c.customerId}>
                    <td className="px-4 py-2 text-slate-800 truncate">{c.name}</td>
                    <td className="px-4 py-2 text-right text-slate-400 text-[11.5px] whitespace-nowrap">{c.count} inv</td>
                    <td className="px-4 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">{money2(c.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================ CUSTOMER PAYMENTS ============================
function CustomerPaymentsPanel({ invoices, onOpenInvoice }) {
  const payments = useMemo(() => {
    const out = [];
    invoices.forEach((inv) => {
      (inv.payments || []).forEach((p) => {
        out.push({
          id: p.id, date: p.receivedAt, customer: inv.customer, customerId: inv.customerId,
          invoiceId: inv.id, invoiceNumber: inv.number || inv.id,
          method: p.method, reference: p.reference || "", amount: Number(p.amount) || 0,
        });
      });
    });
    return out.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [invoices]);

  const total = payments.reduce((s, p) => s + p.amount, 0);

  const exportCsv = () => {
    downloadCsv("customer-payments.csv",
      ["Date", "Customer", "Customer #", "Invoice #", "Method", "Reference", "Amount"],
      payments.map((p) => [fmtDate(p.date), p.customer, p.customerId, p.invoiceNumber, p.method, p.reference, p.amount.toFixed(2)]));
    toast.success(`Exported ${payments.length} payments`);
  };

  return (
    <div data-testid="accounting-payments-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[22px] font-bold text-slate-900">Customer Payments</div>
          <div className="text-[12.5px] text-slate-500">{payments.length} payments · {money2(total)} received</div>
        </div>
        <ExportBtn onClick={exportCsv} disabled={payments.length === 0} />
      </div>
      {payments.length === 0 ? (
        <div className="border border-slate-200 rounded-lg py-12 text-center text-[13px] text-slate-400 italic">No payments recorded yet.</div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                <th className="px-3 py-2.5">Date</th>
                <th className="px-3 py-2.5">Customer</th>
                <th className="px-3 py-2.5">Invoice #</th>
                <th className="px-3 py-2.5">Method</th>
                <th className="px-3 py-2.5">Reference</th>
                <th className="px-3 py-2.5 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => (
                <tr key={p.id} data-testid={`accounting-payment-row-${p.id}`} className="hover:bg-blue-50/30">
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{fmtDate(p.date)}</td>
                  <td className="px-3 py-2.5 text-slate-800 truncate max-w-[200px]">{p.customer}</td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => onOpenInvoice?.(p.invoiceId)} className="text-blue-600 font-semibold hover:underline">{p.invoiceNumber}</button>
                  </td>
                  <td className="px-3 py-2.5"><span className="text-[10.5px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{p.method}</span></td>
                  <td className="px-3 py-2.5 text-slate-500">{p.reference || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-emerald-700 whitespace-nowrap">{money2(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================ BANK DEPOSITS ============================
function BankDepositsPanel({ invoices }) {
  const deposits = useMemo(() => {
    const m = new Map(); // `${day}|${method}` -> {date, method, count, total}
    invoices.forEach((inv) => {
      (inv.payments || []).forEach((p) => {
        if (!p.receivedAt) return;
        const day = dayKey(p.receivedAt);
        const key = `${day}|${p.method}`;
        const row = m.get(key) || { date: day, method: p.method, count: 0, total: 0 };
        row.count += 1; row.total += Number(p.amount) || 0;
        m.set(key, row);
      });
    });
    return Array.from(m.values()).sort((a, b) => b.date.localeCompare(a.date) || a.method.localeCompare(b.method));
  }, [invoices]);

  const total = deposits.reduce((s, d) => s + d.total, 0);
  const exportCsv = () => {
    downloadCsv("bank-deposits.csv",
      ["Deposit Date", "Method", "# Payments", "Total"],
      deposits.map((d) => [fmtDate(d.date), d.method, d.count, d.total.toFixed(2)]));
    toast.success(`Exported ${deposits.length} deposit batches`);
  };

  return (
    <div data-testid="accounting-deposits-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[22px] font-bold text-slate-900">Bank Deposits</div>
        <ExportBtn onClick={exportCsv} disabled={deposits.length === 0} />
      </div>
      <div className="text-[12.5px] text-slate-500 mb-4 flex items-center gap-1.5">
        <Info className="h-3.5 w-3.5 text-slate-400" /> Payments grouped by date &amp; method into deposit batches · {money2(total)} total
      </div>
      {deposits.length === 0 ? (
        <div className="border border-slate-200 rounded-lg py-12 text-center text-[13px] text-slate-400 italic">No deposits to show.</div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                <th className="px-3 py-2.5">Deposit Date</th>
                <th className="px-3 py-2.5">Method</th>
                <th className="px-3 py-2.5 text-right"># Payments</th>
                <th className="px-3 py-2.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {deposits.map((d) => (
                <tr key={`${d.date}-${d.method}`} data-testid={`accounting-deposit-row-${d.date}-${d.method}`} className="hover:bg-blue-50/30">
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{fmtDate(d.date)}</td>
                  <td className="px-3 py-2.5"><span className="text-[10.5px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{d.method}</span></td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{d.count}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900 whitespace-nowrap">{money2(d.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================ BATCH / EXPORT ============================
function BatchExportPanel({ invoices, fromDate, toDate }) {
  const inRange = (iso) => {
    if (!iso) return false;
    if (!fromDate && !toDate) return true;
    const ts = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).getTime();
    if (fromDate && ts < new Date(`${fromDate}T00:00:00`).getTime()) return false;
    if (toDate && ts > new Date(`${toDate}T23:59:59.999`).getTime()) return false;
    return true;
  };
  const scoped = useMemo(() => invoices.filter((inv) => inRange(inv.issuedAt)), [invoices, fromDate, toDate]);
  const paymentCount = useMemo(() => scoped.reduce((s, inv) => s + (inv.payments || []).filter((p) => inRange(p.receivedAt)).length, 0), [scoped, fromDate, toDate]);

  const rangeLabel = fromDate || toDate ? `${fromDate || "start"} → ${toDate || "today"}` : "All time";

  const exportInvoices = () => {
    downloadCsv(`batch-invoices.csv`,
      ["Invoice #", "Date", "Due", "Customer", "Customer #", "Subtotal", "Tax", "Total", "Paid", "Balance", "Status", "Reviewed", "Sent"],
      scoped.map((inv) => [
        inv.number || inv.id, fmtDate(inv.issuedAt), inv.dueAt ? fmtDate(inv.dueAt) : "",
        inv.customer, inv.customerId,
        (Number(inv.subtotal) || 0).toFixed(2), (Number(inv.taxAmount) || 0).toFixed(2), (Number(inv.total) || 0).toFixed(2),
        (Number(inv.amountPaid) || 0).toFixed(2), (Number(inv.balanceDue) || 0).toFixed(2),
        inv.status, inv.reviewed ? "Yes" : "No", inv.lastSentAt ? "Yes" : "No",
      ]));
    toast.success(`Exported ${scoped.length} invoices`);
  };
  const exportPayments = () => {
    const rows = [];
    scoped.forEach((inv) => (inv.payments || []).forEach((p) => {
      if (!inRange(p.receivedAt)) return;
      rows.push([fmtDate(p.receivedAt), inv.number || inv.id, inv.customer, inv.customerId, p.method, p.reference || "", (Number(p.amount) || 0).toFixed(2), p.recordedBy || ""]);
    }));
    downloadCsv(`batch-payments.csv`, ["Date", "Invoice #", "Customer", "Customer #", "Method", "Reference", "Amount", "Recorded By"], rows);
    toast.success(`Exported ${rows.length} payments`);
  };

  return (
    <div data-testid="accounting-batch-panel">
      <div className="text-[22px] font-bold text-slate-900 mb-1">Batch / Export Transactions</div>
      <div className="text-[12.5px] text-slate-500 mb-5 flex items-center gap-1.5">
        <Info className="h-3.5 w-3.5 text-slate-400" /> Export accounting transactions for your bookkeeping software. Range follows the date filter above: <strong className="text-slate-700">{rangeLabel}</strong>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-1"><FileText className="h-4 w-4 text-blue-600" /><span className="text-[14px] font-semibold text-slate-900">Invoices</span></div>
          <div className="text-[12.5px] text-slate-500 mb-3">{scoped.length} invoices in range</div>
          <ExportBtn onClick={exportInvoices} disabled={scoped.length === 0} label="Export Invoices CSV" full />
        </div>
        <div className="rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-1"><Wallet className="h-4 w-4 text-emerald-600" /><span className="text-[14px] font-semibold text-slate-900">Payments</span></div>
          <div className="text-[12.5px] text-slate-500 mb-3">{paymentCount} payments in range</div>
          <ExportBtn onClick={exportPayments} disabled={paymentCount === 0} label="Export Payments CSV" full />
        </div>
      </div>
    </div>
  );
}

// ---------- shared bits ----------
function BigStat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`text-[22px] font-bold ${accent}`}>{value}</div>
    </div>
  );
}
function ExportBtn({ onClick, disabled, label = "Export CSV", full }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid="accounting-export-btn"
      className={`h-8 px-3 rounded-md text-[12.5px] font-semibold border flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 ${full ? "w-full h-9" : ""}`}>
      <FileDown className="h-3.5 w-3.5" /> {label}
    </button>
  );
}
